/* eslint-disable no-unused-vars */
/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Input, Popconfirm, Row, Select, Space, Table, Typography } from "antd";
import { DeleteSvgIcon } from "Icon/Icon";
import { IPlayerRoster } from "interfaces/ShareDesign/IShareDesign";
import { getSize } from "./ShareDesign.Constant";
import { CaretDownOutlined } from "@ant-design/icons";
import { useAppSelector } from "hooks/reduxHooks";
import TemplateInstructions from "./TemplateInstructions";

const { Title } = Typography;

type EditableTableProps = Parameters<typeof Table>[0];
type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>;

interface PlayerRosterProps {
  products: IPlayerRoster[];
  onChangeSelect: (selectType: string, key: number, data: string) => void;
  onHandleAdd: () => void;
  onHandleDelete: (key: number) => void;
  onHandleSave: (row: IPlayerRoster) => void;
  onHandleTextField: (row: IPlayerRoster, data: any) => void;
  handleCSVUpload: (e: any) => void;
  fileInputRef: any;
}

const PlayerRoster = ({
  products,
  onHandleTextField,
  onHandleAdd,
  onChangeSelect,
  onHandleDelete,
  onHandleSave,
  handleCSVUpload,
  fileInputRef,
}: PlayerRosterProps) => {
  const selectRef = useRef<any>(null);
  const [dataSource, setDataSource] = useState<IPlayerRoster[]>([]);
  const [openTemplateInstructions, setOpenTemplateInstructions] = useState<boolean>(false);

  const localPortalDetails: any = useAppSelector(
    (state: any) => state?.landingPage?.lockerPortalDetails
  );

  const attributes: any = localPortalDetails?.lockerPortalDetails?.Attributes?.Attributes.filter(
    (item: any) => item?.AttributeTypeName === "Multi Select"
  );

  useEffect(() => {
    const handleWheelScroll = () => {
      if (selectRef.current) {
        selectRef.current.blur();
      }
    };

    window.addEventListener("wheel", handleWheelScroll);
    return () => {
      window.removeEventListener("wheel", handleWheelScroll);
    };
  }, []);

  /* defined columns with default values */
  const defaultColumns: (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] = [
    {
      title: <label className="table-head">Player Name</label>,
      dataIndex: "playerName",
      key: "playerName",
      // width: "20%",
      editable: true,
      render: (text: string, record: any) => (
        <Input
          name="playerName"
          defaultValue={text}
          className="player-name-input"
          data-test-selector={`txtPlayerName${text}`}
          onChange={(e: any) => onHandleTextField(record, { [e.target.name]: e.target.value })}
        />
      ),
    },
    {
      title: <label className="table-head">Uniform No.</label>,
      dataIndex: "uniformNo",
      key: "uniformNo",
      render: (text: string, record: any) => {
        const onlyNumber = (number: string) => {
          const regex = /^(?:0\d?|0[02]|(?:\d|[1-9]\d))$/;
          if (number && !regex.test(number)) {
            return true;
          }
          return false;
        };
        return (
          <>
            <Input
              name="uniformNo"
              defaultValue={text}
              className="player-name-input"
              data-test-selector={`txtPlayerName${text}`}
              onChange={(e: any) => onHandleTextField(record, { [e.target.name]: e.target.value })}
            />
            <span style={{ color: "red", fontWeight: "600", fontSize: "12px" }}>
              {" "}
              {onlyNumber(text) ? "Two digits only" : ""}
            </span>
          </>
        );
      },
    },
    {
      title: <label className="table-head">Jersey Size</label>,
      dataIndex: "jerseySize",
      key: "jerseySize",
      render: (_: any, record: any) => {
        return (
          <Select
            showSearch
            ref={selectRef}
            placeholder="Select a size"
            optionFilterProp="children"
            filterOption={filterOption}
            data-test-selector="drpJerseySize"
            suffixIcon={
              <CaretDownOutlined style={{ pointerEvents: "none", color: "rgba(0, 0, 0, 0.88)" }} />
            }
            defaultValue={record?.jerseySize}
            onChange={(e: any) => onChangeSelect("jerseySize", record?.key, e)}
            options={getSize(attributes, "JerseySize")}
          />
        );
      },
    },
    {
      title: <label className="table-head">Pant Size</label>,
      dataIndex: "pantSize",
      key: "pantSize",
      render: (_: any, record: any) => (
        <Select
          showSearch
          ref={selectRef}
          placeholder="Select a size"
          optionFilterProp="children"
          filterOption={filterOption}
          data-test-selector="drpPantSize"
          suffixIcon={
            <CaretDownOutlined style={{ pointerEvents: "none", color: "rgba(0, 0, 0, 0.88)" }} />
          }
          defaultValue={record?.pantSize}
          onChange={(e: any) => onChangeSelect("pantSize", record?.key, e)}
          options={getSize(attributes, "PantSize")}
        />
      ),
    },
    {
      title: <label className="table-head">Helmet Size</label>,
      dataIndex: "helmetSize",
      key: "helmetSize",
      render: (_: any, record: any) => (
        <Select
          showSearch
          ref={selectRef}
          placeholder="Select a size"
          optionFilterProp="children"
          filterOption={filterOption}
          data-test-selector="drpHelmetSize"
          defaultValue={record?.helmetSize}
          suffixIcon={
            <CaretDownOutlined style={{ pointerEvents: "none", color: "rgba(0, 0, 0, 0.88)" }} />
          }
          onChange={(e: any) => onChangeSelect("helmetSize", record?.key, e)}
          options={getSize(attributes, "HelmetSize")}
        />
      ),
    },
    {
      title: <label className="table-head">Inseam</label>,
      dataIndex: "inseam",
      key: "inseam",
      render: (_: any, record: any) => (
        <Select
          showSearch
          ref={selectRef}
          placeholder="Select a size"
          optionFilterProp="children"
          filterOption={filterOption}
          data-test-selector="drpInseam"
          defaultValue={record?.inseam}
          suffixIcon={
            <CaretDownOutlined style={{ pointerEvents: "none", color: "rgba(0, 0, 0, 0.88)" }} />
          }
          onChange={(e: any) => onChangeSelect("inseam", record?.key, e)}
          options={getSize(attributes, "Inseam")}
        />
      ),
    },
    {
      title: <label className="table-head"></label>,
      dataIndex: "action",
      className: "record-action",
      width: "4%",
      render: (_: any, record: any) => (
        <Popconfirm
          overlayClassName="delete-modal-style"
          title="Sure to delete?"
          onConfirm={() => onHandleDelete(record.key)}
        >
          <DeleteSvgIcon />
        </Popconfirm>
      ),
    },
  ];

  /* defined columns with editable cell */
  const columns = defaultColumns.map((col: any) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IPlayerRoster) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        onHandleSave,
      }),
    };
  });

  /* format select field labels */
  const filterOption = (input: string, option?: { label: string; value: string }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const downloadTemplate = () => {
    const header =
      ["PlayerName", "UniformNo", "JerseySize", "PantSize", "HelmetSize", "Inseam"] + "\n";
    const blob = new Blob([header], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "PlayerRoster.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    setDataSource(products);
  }, [products]);

  return (
    <div className="player-roaster-information">
      <Space size={50} className="player-roaster-header flex-xs-column">
        <Title level={4} className="section-heading">
          Player Roster
        </Title>

        <Row
          gutter={[
            { xs: 15, sm: 15, md: 15, lg: 35 },
            { xs: 15, sm: 15, md: 15, lg: 35 },
          ]}
          className="roaster-header-action"
        >
          <Col xs={24} lg={6} xl={6} xxl={6} md={6}>
            <div className="upload-docs">
              <input
                type="file"
                id="uploadCSV"
                ref={fileInputRef}
                onChange={handleCSVUpload}
                style={{ display: "none" }}
                data-test-selector="inpUploadCsv"
              />
              <Button type="text" className="action-button" data-test-selector={"btnImportRoster"}>
                <label htmlFor="uploadCSV">Import Roster( csv )</label>
              </Button>
            </div>
          </Col>
          <Col xs={24} lg={8} xl={8} xxl={8} md={8}>
            <Button
              type="text"
              className="action-button"
              data-test-selector={"btnDownloadImportRoster"}
              onClick={downloadTemplate}
            >
              Download Import Template
            </Button>
          </Col>
          <Col xs={24} lg={8} xl={8} xxl={8} md={8}>
            <Button
              type="text"
              className="action-button"
              data-test-selector={"btnTemplateInstruction"}
              onClick={() => setOpenTemplateInstructions(true)}
            >
              Template Instructions
            </Button>
            <TemplateInstructions
              open={openTemplateInstructions}
              onClose={() => setOpenTemplateInstructions(false)}
            />
          </Col>
        </Row>
      </Space>
      <div className="player-roster-table-wrapper mt-3">
        <Table
          bordered={false}
          pagination={false}
          scroll={{ y: 450 }}
          dataSource={dataSource}
          columns={columns as ColumnTypes}
          data-test-selector="tblPlayerRoster"
          className="add-player-tbl"
        />
        <div className="table-action">
          <Button
            type="primary"
            className="add-new-player-btn"
            data-test-selector={"btnAddNewPlayer"}
            onClick={onHandleAdd}
          >
            New Player
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlayerRoster;
